import React, { useCallback, useEffect, useRef, useState } from "react";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  DatasetLink,
  addLinkToDataset,
  removeLinkFromDataset,
} from "@/lib/api/datasets";
import { Button } from "@/components/common/Button";
import { TooltipButton } from "@/components/common/TooltipButton";
import { DropdownMenu } from "@/components/common/DropdownMenu";
import { NumberCount } from "@/components/common/NumberCount";
import { Tooltip } from "@/components/common/Tooltip";
import { TextInput } from "@/components/common/TextInput";
import {
  DatasetMetaSection,
  MetaAddButton,
} from "@/components/main/datasets/DatasetMetaSection";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

const MAX_LINKS = 20;
const MAX_URL_LEN = 2000;

const XSMALL = { fontSize: "var(--font-size-x-small)" } as const;

/// Strip protocol + leading www and a trailing slash, then truncate — the chip
/// fallback when a link has no label.
const shortenUrl = (u: string): string => {
  try {
    const url = new URL(u);
    const s = url.host.replace(/^www\./, "") + url.pathname.replace(/\/$/, "");
    return s.length > 40 ? `${s.slice(0, 39)}…` : s;
  } catch {
    return u;
  }
};

const httpErrorMessage = (err: unknown): string | null => {
  const status = (
    err as { response?: { status?: number; data?: { error?: string } } }
  )?.response?.status;
  const code = (err as { response?: { data?: { error?: string } } })?.response
    ?.data?.error;
  if (status === 409) return "Already added";
  if (status === 400) {
    if (code === "invalid_url_scheme")
      return "Only http and https links are allowed";
    if (code === "dataset_links_limit_reached")
      return `Limit of ${MAX_LINKS} reached`;
    return "Enter a valid http(s) URL";
  }
  return null;
};

interface DatasetLinksProps {
  datasetId: string;
  /// The persisted links (owned by the parent row / page).
  links: DatasetLink[];
  /// Owners get add/remove affordances; viewers see read-only chips.
  canEdit: boolean;
  /// Bubble the new link list up so the row/page keeps the latest copy.
  onLinksChange: (links: DatasetLink[]) => void;
  /// Right padding, forwarded to the section shell (defaults to the dataset-row
  /// `pr-2`; the row passes `pr-0` to align the add pill with the search bar).
  pr?: string;
}

/// Related links for a dataset. Chips wrap onto new lines so the section grows
/// vertically like the other subsections (Description, Tables) instead of
/// scrolling sideways; the "+ Add link" pill lives on the heading row. The
/// inline composer stays open after a successful add so several links go in
/// with one click.
export const DatasetLinks: React.FC<DatasetLinksProps> = ({
  datasetId,
  links,
  canEdit,
  onLinksChange,
  pr,
}) => {
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLDivElement>(null);

  const atCap = links.length >= MAX_LINKS;

  useEffect(() => {
    if (adding) composerRef.current?.querySelector("input")?.focus();
  }, [adding]);

  const openComposer = useCallback(() => {
    setError(null);
    setAdding(true);
  }, []);

  const closeComposer = useCallback(() => {
    setAdding(false);
    setUrl("");
    setLabel("");
    setError(null);
  }, []);

  const canSubmit =
    !!url.trim() && url.trim().length <= MAX_URL_LEN && !atCap && !submitting;

  const submit = useCallback(async () => {
    // Let people paste a bare host ("stripe.com/docs") — assume https. Only when
    // there's no scheme at all (so "javascript:…" still trips the protocol gate)
    // AND the input actually looks like a host: a dot-separated authority, or
    // localhost. A bare word like "test" is not a link, so it falls through to
    // URL validation below and is rejected rather than saved as "https://test".
    const typed = url.trim();
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(typed);
    const looksLikeHost =
      /^[^\s/]+\.[^\s/]+/.test(typed) || /^localhost([:/]|$)/i.test(typed);
    const raw =
      typed && !hasScheme && looksLikeHost ? `https://${typed}` : typed;
    if (!raw) return setError("Enter a URL");
    if (raw.length > MAX_URL_LEN) return setError("URL is too long");
    let parsed: URL;
    try {
      parsed = new URL(raw);
    } catch {
      return setError("Enter a valid http(s) URL");
    }
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return setError("Only http and https links are allowed");
    }
    if (links.some((l) => l.url === raw)) return setError("Already added");
    if (links.length >= MAX_LINKS)
      return setError(`Limit of ${MAX_LINKS} reached`);

    setSubmitting(true);
    try {
      const created = await addLinkToDataset(datasetId, {
        url: raw,
        label: label.trim() || undefined,
      });
      onLinksChange([...links, created]);
      // keep the composer open + refocus so adding several is one click each
      setUrl("");
      setLabel("");
      setError(null);
      composerRef.current?.querySelector("input")?.focus();
    } catch (err) {
      const fieldMsg = httpErrorMessage(err);
      if (fieldMsg) setError(fieldMsg);
      else showNotificationGlobal("Failed to add link", "error");
    } finally {
      setSubmitting(false);
    }
  }, [url, label, links, datasetId, onLinksChange]);

  const remove = useCallback(
    async (linkId: string) => {
      try {
        await removeLinkFromDataset(datasetId, linkId);
        onLinksChange(links.filter((l) => l.id !== linkId));
      } catch {
        showNotificationGlobal("Failed to remove link", "error");
      }
    },
    [datasetId, links, onLinksChange],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
      e.preventDefault();
      if (canSubmit) void submit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeComposer();
    }
  };

  // Viewers with no links to show: render nothing. Owners always keep their
  // add affordance, so they render even when empty.
  if (!canEdit && links.length === 0) return null;

  return (
    <>
      <DatasetMetaSection
        icon={<LinkOutlinedIcon className="icon-sm" />}
        label="Related links"
        pr={pr}
        collapsible
        defaultCollapsed
        itemCount={links.length}
        meta={
          links.length > 0 ? (
            atCap ? (
              <span
                className="font-mono"
                style={{ ...XSMALL, color: "var(--color-warning)" }}
              >
                {`${links.length} / ${MAX_LINKS}`}
              </span>
            ) : (
              <NumberCount count={links.length} size="sm" />
            )
          ) : undefined
        }
        action={
          canEdit ? (
            <div ref={addBtnRef}>
              <MetaAddButton
                label={links.length === 0 ? "Add a related link" : "Add link"}
                onClick={adding ? closeComposer : openComposer}
                disabled={atCap}
                title={atCap ? `Limit of ${MAX_LINKS} reached` : undefined}
              />
            </div>
          ) : undefined
        }
      >
        {links.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            {links.map((link) => (
              <div
                key={link.id}
                className="group/chip inline-flex items-center max-w-[320px] min-h-(--control-height-sm) px-2 py-0.5 rounded-app border border-border text-fg select-none hover:border-accent hover:bg-bg-subtle transition-colors"
              >
                <Tooltip title={link.url} placement="top">
                  <button
                    type="button"
                    onClick={() =>
                      window.open(link.url, "_blank", "noopener,noreferrer")
                    }
                    className="inline-flex items-center min-w-0 cursor-pointer"
                    style={{
                      fontSize: "var(--font-size-small)",
                      lineHeight: 1.2,
                    }}
                  >
                    <span className="truncate">
                      {link.label ?? shortenUrl(link.url)}
                    </span>
                    <OpenInNewOutlinedIcon className="icon-xs text-fg-muted shrink-0 overflow-hidden opacity-0 max-w-0 group-hover/chip:opacity-80 group-hover/chip:max-w-3 group-hover/chip:ml-1 transition-all duration-200" />
                  </button>
                </Tooltip>
                {canEdit && (
                  <span className="inline-flex shrink-0 overflow-hidden opacity-0 max-w-0 group-hover/chip:opacity-100 group-hover/chip:max-w-7 group-hover/chip:ml-0.5 transition-all duration-200">
                    <TooltipButton
                      title="Remove"
                      aria-label={`Remove ${link.label ?? shortenUrl(link.url)}`}
                      onClick={() => void remove(link.id)}
                      variant="contextual"
                      size="xs"
                    >
                      <CloseOutlinedIcon />
                    </TooltipButton>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </DatasetMetaSection>
      {canEdit && (
        <DropdownMenu
          anchorRef={addBtnRef}
          isOpen={adding}
          onClose={closeComposer}
          placement="bottom-end"
          style={{ width: "18rem" }}
        >
          <div
            ref={composerRef}
            onKeyDown={onKeyDown}
            className="flex flex-col gap-2 p-3"
          >
            <TextInput
              name="dataset-link-url"
              value={url}
              onChange={(v) => {
                setUrl(v);
                if (error) setError(null);
              }}
              placeholder="Paste a link — stripe.com/docs"
            />
            <TextInput
              name="dataset-link-label"
              value={label}
              onChange={setLabel}
              placeholder="Label (optional)"
            />
            <span
              style={{
                ...XSMALL,
                color: error ? "var(--color-error)" : "var(--color-fg-muted)",
              }}
            >
              {error ?? "Enter to add · Esc to close"}
            </span>
            <Button
              variant="primary"
              size="md"
              loading={submitting}
              disabled={!canSubmit}
              onClick={() => void submit()}
            >
              Add
            </Button>
          </div>
        </DropdownMenu>
      )}
    </>
  );
};
